import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Alert,
  Snackbar,
  Chip,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { productionApi } from '../api/production';
import { shortfallApi } from '../services/api';
import jobWorksApi from '../api/jobWorks';
import designApi from '../api/design';
import api from '../services/api';
import DesignCuttingRecordDialog from './DesignCuttingRecordDialog';

interface StitchingCompletionDialogProps {
  open: boolean;
  designCutting: any;
  onClose: () => void;
  onComplete: () => void;
  viewOnly?: boolean; // New prop to make dialog view-only
  onOpenCuttingForm?: (designNo: string, readOnly: boolean) => void;
}

interface WorkerAssignment {
  id: number;
  worker_name: string;
  size: string;
  garment_part: string;
  quantity_issued: number;
  issue_date: string;
  receives?: Array<{
    quantity_received: number;
    defect_quantity: number;
    receive_date: string;
  }>;
}

const StitchingCompletionDialog: React.FC<StitchingCompletionDialogProps> = ({
  open,
  designCutting,
  onClose,
  onComplete,
  viewOnly = false,
  onOpenCuttingForm
}) => {
  const [loading, setLoading] = useState(false);
  const [workerAssignments, setWorkerAssignments] = useState<WorkerAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [stitchedPieces, setStitchedPieces] = useState<{ [garmentPart: string]: { [size: string]: number } }>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [jobWork, setJobWork] = useState<any>(null);
  const [loadingJobWork, setLoadingJobWork] = useState(false);
  const [allDesignCuttings, setAllDesignCuttings] = useState<any[]>([]);
  const [receivedItems, setReceivedItems] = useState<any[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [shortfallSummary, setShortfallSummary] = useState<Record<string, { open: number; closed: number }>>({});
  const [shortfallInputs, setShortfallInputs] = useState<Record<string, number>>({});
  const [showCuttingRecordDialog, setShowCuttingRecordDialog] = useState(false);

  // Size order for consistent display
  const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
  
  // Garment part order
  const garmentPartOrder = ['TOP', 'BOTTOM', 'JACKET', 'DUPATTA', 'BELT', 'OTHER'];

  // Calculate days since job issued and days since assignment
  const calculateDays = (dateString: string) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    if (open && designCutting) {
      fetchAllData();
    } else if (!open) {
      // Reset state when dialog closes
      setStitchedPieces({});
      setAllDesignCuttings([]);
      setWorkerAssignments([]);
      setReceivedItems([]);
      setJobWork(null);
    }
  }, [open, designCutting]);

  // Initialize stitched pieces when all data is loaded
  useEffect(() => {
    if (allDesignCuttings.length > 0 && !loadingData && !loadingAssignments) {
      console.log('Initializing stitched pieces with data:', {
        allDesignCuttings,
        workerAssignments,
        receivedItems
      });
      initializeStitchedPieces();
    }
  }, [allDesignCuttings, workerAssignments, receivedItems, loadingData, loadingAssignments]);

  const fetchAllData = async () => {
    if (!designCutting?.design_no) return;
    
    try {
      setLoadingData(true);
      await Promise.all([
        fetchJobWork(),
        fetchAllDesignCuttings(),
        fetchWorkerAssignments(),
        fetchReceivedItems()
      ]);
      
      setLoadingData(false);
      // Fetch shortfall summary for this design after core data
      try {
        const summary = await shortfallApi.summary(designCutting.design_no);
        setShortfallSummary(summary || {});
      } catch (e) {
        console.warn('Failed to fetch shortfall summary');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingData(false);
    }
  };

  const fetchAllDesignCuttings = async () => {
    if (!designCutting?.design_no) return;
    
    try {
      const response = await api.get('/design-cutting');
      const cuttings = response.data.filter((c: any) => 
        c.design_no === designCutting.design_no && (c.status === 'RECEIVED' || c.status === 'COMPLETED')
      );
      console.log('Fetched design cuttings:', cuttings);
      setAllDesignCuttings(cuttings);
    } catch (error) {
      console.error('Error fetching design cuttings:', error);
    }
  };

  const fetchReceivedItems = async () => {
    if (!designCutting?.design_no) return;
    
    try {
      const response = await api.get(`/job-works/by-design/${designCutting.design_no}`);
      const jobWorks = response.data.filter((jw: any) => jw.status === 'RECEIVED' || jw.status === 'COMPLETED');
      
      const allItems: any[] = [];
      jobWorks.forEach((jw: any) => {
        if (jw.receivedItems) {
          allItems.push(...jw.receivedItems);
        }
      });
      
      setReceivedItems(allItems);
    } catch (error) {
      console.error('Error fetching received items:', error);
    }
  };

  const fetchJobWork = async () => {
    if (!designCutting?.design_no) return;
    
    try {
      setLoadingJobWork(true);
      const response = await api.get(`/job-works/by-design/${designCutting.design_no}`);
      
      console.log('Fetched job works for design:', response.data);
      
      // Find internal stitching job works
      const relevantJobs = response.data.filter((jw: any) => {
        const jobTypeName = jw.jobType?.name || jw.jobType;
        const isStitching = (jobTypeName === 'STITCHING' || jobTypeName?.toUpperCase() === 'STITCHING');
        const isInternal = jw.vendor?.is_internal === true;
        // Include ISSUED, RECEIVED, and COMPLETED for legacy support
        const validStatus = ['ISSUED', 'RECEIVED', 'COMPLETED'].includes(jw.status);
        
        console.log(`Job ${jw.jobWorkCode}: type=${jobTypeName}, isStitching=${isStitching}, isInternal=${isInternal}, status=${jw.status}`);
        
        return isStitching && isInternal && validStatus;
      });
      
      console.log('Relevant stitching jobs found:', relevantJobs);
      
      if (relevantJobs.length > 0) {
        // Prefer the most recent one
        const selectedJob = relevantJobs[relevantJobs.length - 1];
        console.log('Selected job work:', selectedJob);
        setJobWork(selectedJob);
      } else {
        console.warn('No internal stitching job work found for this design');
      }
    } catch (error) {
      console.error('Error fetching job work:', error);
    } finally {
      setLoadingJobWork(false);
    }
  };

  const fetchWorkerAssignments = async () => {
    if (!designCutting?.design_no) return;

    try {
      setLoadingAssignments(true);
      const response = await api.get('/worker-assignments');
      const assignments = response.data.filter((assignment: any) => 
        assignment.design_no === designCutting.design_no &&
        assignment.jobType === 'STITCHING' &&
        !assignment.is_refinish_job // Exclude refinish assignments
      );
      setWorkerAssignments(assignments);
    } catch (error) {
      console.error('Error fetching worker assignments:', error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const initializeStitchedPieces = () => {
    if (allDesignCuttings.length === 0) {
      console.log('No design cuttings to initialize');
      return;
    }

    console.log('Starting initialization with:', {
      designCuttingsCount: allDesignCuttings.length,
      workerAssignmentsCount: workerAssignments.length,
      receivedItemsCount: receivedItems.length
    });

    const pieces: { [garmentPart: string]: { [size: string]: number } } = {};
    
    // Group design cuttings by garment part
    const garmentParts = [...new Set(allDesignCuttings.map(c => c.garment_part))];
    console.log('Garment parts found:', garmentParts);
    
    garmentParts.forEach(garmentPart => {
      pieces[garmentPart] = {};
      
      // Get all cuttings for this garment part
      const cuttingsForPart = allDesignCuttings.filter(c => c.garment_part === garmentPart);
      
      // Calculate program quantities (total cut) per size
      const programInfo: { [size: string]: number } = {};
      cuttingsForPart.forEach(cutting => {
        const sizeFields = [
          { key: 'xs_pieces', size: 'XS' },
          { key: 's_pieces', size: 'S' },
          { key: 'm_pieces', size: 'M' },
          { key: 'l_pieces', size: 'L' },
          { key: 'xl_pieces', size: 'XL' },
          { key: 'xxl_pieces', size: 'XXL' },
          { key: 'three_xl_pieces', size: '3XL' }
        ];

        sizeFields.forEach(({ key, size }) => {
          const qty = (cutting as any)[key] || 0;
          if (qty > 0) {
            programInfo[size] = (programInfo[size] || 0) + qty;
          }
        });
      });
      
      console.log(`Program info for ${garmentPart}:`, programInfo);
      
      // Calculate worker assignment stats
      const assignmentStats: { [size: string]: { issued: number; received: number } } = {};
      workerAssignments.forEach(wa => {
        if (wa.garment_part === garmentPart) {
          if (!assignmentStats[wa.size]) {
            assignmentStats[wa.size] = { issued: 0, received: 0 };
          }
          assignmentStats[wa.size].issued += wa.quantity_issued || 0;
          
          // Sum up all receives for this assignment
          // Only count quantity_received, NOT defect_quantity
          // Defects are tracked separately and can be re-assigned later if fabric is found
          if (wa.receives) {
            wa.receives.forEach(receive => {
              assignmentStats[wa.size].received += receive.quantity_received || 0;
            });
          }
        }
      });
      
      console.log(`Assignment stats for ${garmentPart}:`, assignmentStats);
      
      // Calculate already received per size
      const alreadyReceivedForPart: { [size: string]: number } = {};
      receivedItems.forEach(item => {
        if (item.garmentPart === garmentPart) {
          alreadyReceivedForPart[item.size] = (alreadyReceivedForPart[item.size] || 0) + item.pieceCount;
        }
      });
      
      console.log(`Already received for ${garmentPart}:`, alreadyReceivedForPart);
      
      // For each size, calculate pending and set as default
      Object.keys(programInfo).forEach(size => {
        const stats = assignmentStats[size] || { issued: 0, received: 0 };
        const alreadyCompleted = alreadyReceivedForPart[size] || 0;
        
        // If there are worker assignments, use received - already completed
        // Otherwise (legacy records), use program quantity - already completed
        let pendingToComplete = 0;
        
        if (stats.received > 0) {
          // Normal flow: has worker receives
          pendingToComplete = Math.max(0, stats.received - alreadyCompleted);
          console.log(`${garmentPart} ${size}: received=${stats.received}, alreadyCompleted=${alreadyCompleted}, pending=${pendingToComplete}`);
        } else if (programInfo[size] > 0) {
          // Legacy flow: no worker receives, use program quantity
          pendingToComplete = Math.max(0, programInfo[size] - alreadyCompleted);
          console.log(`${garmentPart} ${size} [LEGACY]: programQty=${programInfo[size]}, alreadyCompleted=${alreadyCompleted}, pending=${pendingToComplete}`);
        }
        
        if (pendingToComplete > 0) {
          pieces[garmentPart][size] = pendingToComplete;
        }
      });
    });

    console.log('Final initialized pieces:', pieces);
    setStitchedPieces(pieces);
  };

  const handleStitchedPiecesChange = (garmentPart: string, size: string, value: string) => {
    const numValue = parseInt(value) || 0;
    setStitchedPieces(prev => ({
      ...prev,
      [garmentPart]: {
        ...(prev[garmentPart] || {}),
        [size]: numValue
      }
    }));
  };

  const handleComplete = async () => {
    if (!designCutting?.design_no) return;

    // Collect all garment parts that have values entered
    const completionRequests = [];
    
    for (const garmentPart in stitchedPieces) {
      const pieces = stitchedPieces[garmentPart];
      const totalForPart = Object.values(pieces).reduce((sum, val) => sum + val, 0);
      
      if (totalForPart > 0) {
        // Find the design cutting record for this garment part
        const designCuttingRecord = allDesignCuttings.find(
          dc => dc.garment_part === garmentPart && dc.design_no === designCutting.design_no
        );
        
        if (designCuttingRecord) {
          completionRequests.push({
            design_cutting_id: designCuttingRecord.id,
            stitched_pieces: pieces,
            garmentPart
          });
        }
      }
    }
    
    if (completionRequests.length === 0) {
      setError('Please enter at least some stitched pieces');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Complete all garment parts that have values
      await Promise.all(
        completionRequests.map(req => 
          productionApi.completeStitching({
            design_cutting_id: req.design_cutting_id,
            stitched_pieces: req.stitched_pieces
          })
        )
      );

      setSuccess(`Stitching completed successfully for ${completionRequests.length} garment part(s)!`);
      setTimeout(() => {
        onComplete();
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error('Error completing stitching:', error);
      setError(error.response?.data?.error || 'Failed to complete stitching');
    } finally {
      setLoading(false);
    }
  };

  if (!designCutting) return null;

  const designImage = designCutting.design?.image_url || 
                      designCutting.design?.image2_url || 
                      designCutting.design?.image3_url || 
                      designCutting.design?.image4_url;

  const daysSinceJobIssued = jobWork?.issueDate ? calculateDays(jobWork.issueDate) : 0;
  const oldestAssignment = workerAssignments.reduce((oldest, curr) => {
    if (!oldest || new Date(curr.issue_date) < new Date(oldest.issue_date)) {
      return curr;
    }
    return oldest;
  }, null as WorkerAssignment | null);
  const daysSinceFirstAssignment = oldestAssignment ? calculateDays(oldestAssignment.issue_date) : 0;

  // Group data by garment part
  const garmentParts = [...new Set(allDesignCuttings.map(c => c.garment_part))].sort((a, b) => {
    return garmentPartOrder.indexOf(a) - garmentPartOrder.indexOf(b);
  });


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        {viewOnly ? 'Stitching Status' : 'Complete Stitching'} - {designCutting.design_no}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {loadingData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
        <Grid container spacing={3}>
          {/* Left Column - Image and Basic Info */}
          <Grid item xs={12} md={4}>
            {/* Design Image */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                height: 400,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default'
              }}
            >
              {designImage ? (
                <img
                  src={designImage}
                  alt={designCutting.design_no}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <Typography color="text.secondary">
                  No image available
                </Typography>
              )}
            </Paper>

            {/* Basic Info */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Design Information
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Design No</TableCell>
                    <TableCell>{designCutting.design_no}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Batch Number</TableCell>
                    <TableCell>{designCutting.batch_number}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Received Date</TableCell>
                    <TableCell>
                      {designCutting.received_date 
                        ? new Date(designCutting.received_date).toLocaleDateString('en-IN')
                        : '-'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell>
                      <Chip 
                        label={designCutting.status} 
                        size="small" 
                        color={designCutting.status === 'COMPLETED' ? 'success' : 'warning'} 
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => {
                    if (designCutting?.design_no && typeof (onOpenCuttingForm) === 'function') {
                      onOpenCuttingForm(designCutting.design_no, !!viewOnly);
                      onClose();
                    } else {
                      setShowCuttingRecordDialog(true);
                    }
                  }}
                  fullWidth
                >
                  Open Design Cutting Form
                </Button>
              </Box>
            {/* Legacy Final Complete - Only show if not view-only */}
            {/* Legacy manual completion intentionally removed (auto-calculated stitching status) */}
            </Paper>

            {/* Stitching Timeline */}
            {(jobWork || workerAssignments.length > 0) && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Stitching Timeline
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {jobWork && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Days since job issued
                      </Typography>
                      <Typography variant="h4" color="primary">
                        {daysSinceJobIssued} days
                      </Typography>
                    </Box>
                  )}
                  {workerAssignments.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Days since first assignment
                      </Typography>
                      <Typography variant="h4" color="secondary">
                        {daysSinceFirstAssignment} days
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}
          </Grid>

          {/* Right Column - Program Info and Worker Assignments by Garment Part */}
          <Grid item xs={12} md={8}>
            {garmentParts.map(garmentPart => {
              const cuttingsForPart = allDesignCuttings.filter(c => c.garment_part === garmentPart);
              // Filter out refinish assignments
              const assignmentsForPart = workerAssignments.filter((a: any) => 
                a.garment_part === garmentPart && !a.is_refinish_job
              );
              
              // Fix double counting: Use only the latest batch per garment part
              // Group by batch_number and use the highest batch_number (latest batch)
              const latestBatchByPart = cuttingsForPart.reduce((latest, cutting) => {
                const currentBatch = (cutting as any).batch_number || 1;
                const latestBatch = (latest as any)?.batch_number || 1;
                // Prefer COMPLETED status, otherwise use latest batch number
                if (!latest || 
                    (cutting.status === 'COMPLETED' && latest.status !== 'COMPLETED') ||
                    (cutting.status === latest.status && currentBatch > latestBatch) ||
                    (cutting.status !== 'COMPLETED' && latest.status !== 'COMPLETED' && currentBatch > latestBatch)) {
                  return cutting;
                }
                return latest;
              }, null as any);
              
              // Use only the latest batch record to avoid double counting
              const cuttingToUse = latestBatchByPart || (cuttingsForPart.length > 0 ? cuttingsForPart[0] : null);
              
              // Aggregate program info (from job work) and cut info (from design cutting) by size
              const programInfo: { [size: string]: number } = {};
              const cutInfo: { [size: string]: number } = {};
              
              if (cuttingToUse) {
                const sizeFields = [
                  { key: 'xs_pieces', size: 'XS' },
                  { key: 's_pieces', size: 'S' },
                  { key: 'm_pieces', size: 'M' },
                  { key: 'l_pieces', size: 'L' },
                  { key: 'xl_pieces', size: 'XL' },
                  { key: 'xxl_pieces', size: 'XXL' },
                  { key: 'three_xl_pieces', size: '3XL' }
                ];

                sizeFields.forEach(({ key, size }) => {
                  const qty = cuttingToUse[key] || 0;
                  if (qty > 0) {
                    // Cut info is from design cutting records (single record, no double counting)
                    cutInfo[size] = qty;
                    // Program info (same as cut for now, but kept for consistency)
                    programInfo[size] = qty;
                  }
                });
              }

              // Sort sizes (use cutInfo as primary source, fallback to programInfo)
              const allSizes = [...new Set([...Object.keys(cutInfo), ...Object.keys(programInfo)])];
              const sortedSizes = allSizes.sort((a, b) => 
                sizeOrder.indexOf(a) - sizeOrder.indexOf(b)
              );

              // Calculate assignment stats by size (refinish assignments already filtered out)
              const assignmentStats: { [size: string]: { issued: number; received: number } } = {};
              assignmentsForPart.forEach(assignment => {
                const size = assignment.size;
                if (!assignmentStats[size]) {
                  assignmentStats[size] = { issued: 0, received: 0 };
                }
                assignmentStats[size].issued += assignment.quantity_issued || 0;
                
                if (assignment.receives) {
                  assignment.receives.forEach(receive => {
                    // Only count quantity_received, NOT defect_quantity
                    // Defects are tracked separately and can be re-assigned later if fabric is found
                    assignmentStats[size].received += receive.quantity_received || 0;
                  });
                }
              });

              const totalProgram = sortedSizes.reduce((sum, size) => sum + programInfo[size], 0);

              return (
                <Box key={garmentPart} sx={{ mb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Chip label={garmentPart} color="primary" />
                      <Typography variant="h6" color="primary">
                        Program Information & Worker Assignments
                      </Typography>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Size</strong></TableCell>
                            <TableCell align="right"><strong>Program Qty</strong></TableCell>
                            <TableCell align="right"><strong>Cut Qty</strong></TableCell>
                            <TableCell align="right"><strong>Issued to Workers</strong></TableCell>
                            <TableCell align="right"><strong>Received from Workers</strong></TableCell>
                            <TableCell align="right"><strong>Pending</strong></TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'warning.light' }}>
                              <strong>Shortfall</strong>
                            </TableCell>
                            {!viewOnly && (
                            <TableCell align="right" sx={{ bgcolor: 'success.light' }}>
                              <strong>Complete Now</strong>
                            </TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedSizes.map(size => {
                            const programQty = programInfo[size] || 0;
                            const cutQty = cutInfo[size] || 0;
                            const stats = assignmentStats[size] || { issued: 0, received: 0 };
                            const closedShort = shortfallSummary[`${garmentPart}|${size}`]?.closed || 0;
                            
                            // Calculate pending: Cut - Received - ClosedShortfall
                            const pendingToComplete = Math.max(0, cutQty - stats.received - closedShort);
                            
                            return (
                              <TableRow key={size}>
                                <TableCell><strong>{size}</strong></TableCell>
                                <TableCell align="right">{programQty}</TableCell>
                                <TableCell align="right">{cutQty}</TableCell>
                                <TableCell align="right">{stats.issued}</TableCell>
                                <TableCell align="right">{stats.received}</TableCell>
                                <TableCell align="right">
                                  <Chip 
                                    label={pendingToComplete} 
                                    size="small" 
                                    color={pendingToComplete > 0 ? 'warning' : 'success'}
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ bgcolor: 'warning.light' }}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={shortfallInputs[`${garmentPart}|${size}`] || 0}
                                    onChange={(e) => {
                                      const v = Math.max(0, Math.min(pendingToComplete, parseInt(e.target.value || '0')));
                                      setShortfallInputs(prev => ({ ...prev, [`${garmentPart}|${size}`]: v }));
                                    }}
                                    InputProps={{ inputProps: { min: 0, max: pendingToComplete } }}
                                    sx={{ width: 90 }}
                                    placeholder="0"
                                  />
                                </TableCell>
                                {!viewOnly && (
                                <TableCell align="right" sx={{ bgcolor: 'success.light' }}>
                                  {pendingToComplete > 0 ? (
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={stitchedPieces[garmentPart]?.[size] || 0}
                                      onChange={(e) => handleStitchedPiecesChange(garmentPart, size, e.target.value)}
                                      InputProps={{
                                        inputProps: { min: 0, max: pendingToComplete }
                                      }}
                                      sx={{ width: 80 }}
                                    />
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell><strong>Total</strong></TableCell>
                            <TableCell align="right"><strong>{totalProgram}</strong></TableCell>
                            <TableCell align="right">
                              <strong>
                                {sortedSizes.reduce((sum, size) => sum + (cutInfo[size] || 0), 0)}
                              </strong>
                            </TableCell>
                            <TableCell align="right">
                              <strong>
                                {sortedSizes.reduce((sum, size) => sum + (assignmentStats[size]?.issued || 0), 0)}
                              </strong>
                            </TableCell>
                            <TableCell align="right">
                              <strong>
                                {sortedSizes.reduce((sum, size) => sum + (assignmentStats[size]?.received || 0), 0)}
                              </strong>
                            </TableCell>
                            <TableCell align="right">
                              <strong>
                                {sortedSizes.reduce((sum, size) => {
                                  const cutQty = cutInfo[size] || 0;
                                  const stats = assignmentStats[size] || { issued: 0, received: 0 };
                                  const closedShort = shortfallSummary[`${garmentPart}|${size}`]?.closed || 0;
                                  // Pending = Cut - Received - ClosedShortfall
                                  const pendingToComplete = Math.max(0, cutQty - stats.received - closedShort);
                                  return sum + pendingToComplete;
                                }, 0)}
                              </strong>
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'warning.light' }}>
                              <strong>
                                {sortedSizes.reduce((sum, size) => {
                                  return sum + (shortfallInputs[`${garmentPart}|${size}`] || 0);
                                }, 0)}
                              </strong>
                            </TableCell>
                            {!viewOnly && (
                            <TableCell align="right" sx={{ bgcolor: 'success.light' }}>
                              <strong>
                                {Object.values(stitchedPieces[garmentPart] || {}).reduce((sum, val) => sum + val, 0)}
                              </strong>
                            </TableCell>
                            )}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Show alert for any garment part with pending quantities */}
                    {(() => {
                      const hasPending = sortedSizes.some(size => {
                        const cutQty = cutInfo[size] || 0;
                        const stats = assignmentStats[size] || { issued: 0, received: 0 };
                        
                        // Calculate pending: Cut - Received (simplified logic)
                        const pendingToComplete = Math.max(0, cutQty - stats.received);
                        return pendingToComplete > 0;
                      });
                      
                      const hasWorkerData = sortedSizes.some(size => {
                        const stats = assignmentStats[size] || { issued: 0, received: 0 };
                        return stats.received > 0;
                      });
                      
                      if (!hasPending || viewOnly) return null;
                      
                      return (
                        <Alert severity={hasWorkerData ? "info" : "warning"} sx={{ mt: 2 }}>
                          <strong>{garmentPart}</strong> has pending quantities. 
                          Enter values in the "Complete Now" column above to mark them as completed.
                          {!hasWorkerData && (
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                              ℹ️ Legacy record: Using cut quantities since no worker assignment data is available.
                            </Typography>
                          )}
                        </Alert>
                      );
                    })()}
                  </Paper>
                </Box>
              );
            })}
          </Grid>
        </Grid>
        )}
      </DialogContent>

      <DialogActions>
        {viewOnly ? (
          <>
            <Button 
              variant="outlined"
              disabled={Object.values(shortfallInputs).every(v => !v)}
              onClick={async () => {
                try {
                  setError(null);
                  setSuccess(null);
                  const entries = Object.entries(shortfallInputs).filter(([, v]) => (v || 0) > 0);
                  let savedCount = 0;
                  for (const [key, qty] of entries) {
                    const [part, size] = key.split('|');
                    await shortfallApi.create({
                      design_no: designCutting.design_no,
                      garment_part: part,
                      size,
                      quantity: qty as number,
                      status: 'CLOSED',
                      reason: 'Marked from Stitching Status'
                    });
                    savedCount++;
                  }
                  const summary = await shortfallApi.summary(designCutting.design_no);
                  setShortfallSummary(summary || {});
                  // Keep the input values instead of resetting
                  // setShortfallInputs({});
                  setSuccess(`Shortfall saved successfully! ${savedCount} record(s) created.`);
                } catch (e: any) {
                  setError(e?.response?.data?.error || 'Failed to save shortfall');
                }
              }}
            >
              Save Shortfall
            </Button>
            <Button onClick={onClose} variant="contained">
              Close
            </Button>
          </>
        ) : (
          <>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleComplete}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {loading ? 'Completing...' : 'Complete Stitching'}
        </Button>
          </>
        )}
      </DialogActions>

      {/* Success/Error Messages */}
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Design Cutting Record Dialog */}
      <DesignCuttingRecordDialog
        open={showCuttingRecordDialog}
        designNo={designCutting?.design_no || null}
        onClose={() => setShowCuttingRecordDialog(false)}
      />
    </Dialog>
  );
};

export default StitchingCompletionDialog;
